import React, { useState, useEffect, useRef } from 'react';
import TextInput from 'components/Widget/Input/TextInput';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { GoogleFont } from 'dragonmouse-shared/interfaces';
import forge from 'apis/forge';
import * as S from './styles';

interface FontInputProps {
	font: string; // empty string means no font is selected
	onFontChange: (font: string) => void;
}

const FontInput = ({ font, onFontChange }: FontInputProps) => {
	const [showFontList, setShowFontList] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [fonts, setFonts] = useState<GoogleFont[]>([]);
	const [filteredFonts, setFilteredFonts] = useState<GoogleFont[]>([]);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const loadFonts = async () => {
			try {
				const googleFonts = await forge.getGoogleFonts();
				const sortedFonts = googleFonts.sort((a: any, b: any) => a.family.localeCompare(b.family));
				setFonts(sortedFonts);
				setFilteredFonts(sortedFonts);
			} catch (err) {
				console.error('Error loading fonts:', err);
			}
		};

		loadFonts();
	}, []);

	useEffect(() => {
		if (!searchQuery) {
			setFilteredFonts(fonts);
			return;
		}

		const filtered = fonts.filter((font) =>
			font.family.toLowerCase().includes(searchQuery.toLowerCase())
		);
		setFilteredFonts(filtered);
	}, [searchQuery, fonts]);

	useOnClickOutside(dropdownRef, () => {
		setShowFontList(false);
	});

	const handleFontSelect = (selectedFont: GoogleFont) => {
		onFontChange(selectedFont.family);
		setShowFontList(false);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchQuery(value);
		onFontChange(value);
		setShowFontList(true);
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<S.WrappedBase ref={dropdownRef}>
			<TextInput
				value={font}
				placeholder="Enter font name or description..."
				onChange={handleInputChange}
				onClick={() => {
					setShowFontList(true);
				}}
				onContextMenu={() => setShowFontList(false)}
			/>
			{showFontList && filteredFonts.length > 0 && (
				<S.FontList>
					{filteredFonts.map((font) => (
						<S.FontItem key={font.family} onClick={() => handleFontSelect(font)}>
							{font.family}
						</S.FontItem>
					))}
				</S.FontList>
			)}
		</S.WrappedBase>
	);
};

export default FontInput;
